import { ICON_SIZE } from "components/modal/PreviewDocumentCategory/PreviewComponent/Table/KeyNote";
import { Keynote, KeynoteImage } from "interfaces/models/documentKeynote";
import { KeyplanOrientation } from "interfaces/models/documentKeyplan";
import { flatten, sortBy } from "lodash-es";

const MIN_AREA_RADIUS = 20;

/**
 * It returns the distance between two keynotes
 * @param {Keynote} firstKeyNote - KeyNoteI,
 * @param {Keynote} secondKeyNote - KeyNoteI
 * @returns The distance between two keynotes.
 */
export const distanceBetweenTwoKeyNotes = (
  firstKeyNote: Keynote,
  secondKeyNote: Keynote
) => {
  const deltaX = Math.abs(
    firstKeyNote?.position?.x - secondKeyNote?.position?.x
  );
  const deltaY = Math.abs(
    firstKeyNote?.position?.y - secondKeyNote?.position?.y
  );

  return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
};

const isAreasIncludesKeynote = (
  areas: { [key: number]: Keynote[] },
  keyNote: Keynote
) => {
  return flatten(Object.values(areas)).some(
    (item) => item?.label === keyNote?.label
  );
};

/**
 * It takes an array of keynotes and returns an object with arrays of keynotes grouped by area
 * @param {Keynote[]} keyNotes - KeyNoteI[] - an array of keynotes
 * @returns An object with arrays of keynotes
 */
export const groupKeyNotes = (keyNotes: Keynote[]) => {
  const areas: { [key: number]: Keynote[] } = {};

  for (let i = 0; i < keyNotes.length; i++) {
    areas[i] = [];

    if (isAreasIncludesKeynote(areas, keyNotes?.[i])) {
      delete areas[i];

      continue;
    }

    areas[i].push(keyNotes?.[i]);

    for (let j = i + 1; j < keyNotes.length; j++) {
      if (
        distanceBetweenTwoKeyNotes(keyNotes?.[j], keyNotes?.[i]) <
          MIN_AREA_RADIUS &&
        !isAreasIncludesKeynote(areas, keyNotes?.[j])
      ) {
        areas?.[i]?.push(keyNotes?.[j]);
      }
    }
  }

  return areas;
};

/**
 * The function calculates the maximum navigator length based on the key notes' positions and the
 * container size.
 * @param {Keynote[]} keyNotes
 * @param containerSize
 * @param {KeynoteImage} keyNoteImage
 */
export const getMaxNavigatorLength = (
  keyNotes: Keynote[],
  containerSize: {
    width: number;
    height: number;
  },
  keyNoteImage: KeynoteImage
) => {
  const scaleY =
    Number(containerSize?.height) / Number(keyNoteImage?.imageHeight);
  const scaleX =
    Number(containerSize?.width) / Number(Number(keyNoteImage?.imageWidth));
  const newKeyNotes = keyNotes.map((item) => ({
    ...item,
    position: {
      x: item.position.x * scaleX,
      y: item.position.y * scaleY,
    },
  }));

  const keyNotesSortByYPosition = sortBy(
    newKeyNotes,
    (keyNote) => keyNote.position.y
  );
  const keyNotesSortByXPosition = sortBy(
    newKeyNotes,
    (keyNote) => keyNote.position.x
  );

  const maxNavigatorLength = Math.min(
    keyNotesSortByYPosition?.[0]?.position?.y,
    containerSize.height -
      keyNotesSortByYPosition?.[keyNotesSortByYPosition.length - 1]?.position
        ?.y,
    keyNotesSortByXPosition?.[0]?.position?.x,
    containerSize.width -
      keyNotesSortByXPosition?.[keyNotesSortByXPosition.length - 1]?.position?.x
  );

  return Math.floor(maxNavigatorLength) - ICON_SIZE / 2;
};

/**
 * Check keyplan orientation based on the input angle
 * @param {number} angle - Rotation angle (0, 90, 180, 270)
 * @return {string} 'portrait' or 'landscape'
 */
export const checkKeyPlanOrientationByAngle = (
  angle: number
): KeyplanOrientation => {
  // Normalize angle to ensure it's within [0, 360) range
  const normalizedAngle = ((angle % 360) + 360) % 360;

  // Check rotation angle
  if (normalizedAngle === 0 || normalizedAngle === 180) {
    return KeyplanOrientation.PORTRAIT;
  } else if (normalizedAngle === 90 || normalizedAngle === 270) {
    return KeyplanOrientation.LANDSCAPE;
  } else {
    // For angles that are not multiples of 90
    // Can decide based on the closest range
    if (
      (normalizedAngle > 45 && normalizedAngle < 135) ||
      (normalizedAngle > 225 && normalizedAngle < 315)
    ) {
      return KeyplanOrientation.LANDSCAPE;
    } else {
      return KeyplanOrientation.PORTRAIT;
    }
  }
};
